<template>
  <div class="dashboard-page">
    <section class="dashboard-overview card border-0 rounded-0 bg-white radius-7 mb-25">
      <div class="card-body">
        <div class="dashboard-overview__content">
          <div>
            <span class="dashboard-overview__eyebrow">Dashboard mensal</span>
            <h3 class="dashboard-overview__title">Visão geral do desempenho</h3>
            <p class="dashboard-overview__description">
              Receitas e agendamentos dos usuários que você pode ver.
            </p>
          </div>

          <div class="dashboard-overview__stats">
            <div class="dashboard-overview__stat">
              <span class="dashboard-overview__stat-label">Agendamentos</span>
              <strong>{{ getCardsDashboardMonth.scheduled }}</strong>
            </div>

            <div class="dashboard-overview__stat">
              <span class="dashboard-overview__stat-label">Cancelados</span>
              <strong>{{ getCardsDashboardMonth.canceled }}</strong>
            </div>

            <div class="dashboard-overview__stat dashboard-overview__stat--wide">
              <span class="dashboard-overview__stat-label">Previsto no mês</span>
              <strong>{{ formatCurrency(getMetricsMonthBillingGraphic.invoicing) }}</strong>
              <small class="dashboard-overview__stat-meta">
                Taxas de pagamento: -{{ getCardsDashboardMonth.tax }}
              </small>
            </div>

            <div class="dashboard-overview__stat dashboard-overview__stat--wide">
              <span class="dashboard-overview__stat-label">Despesas do mês</span>
              <strong>R$ {{ getCardsDashboardMonth.expense_value }}</strong>
              <small class="dashboard-overview__stat-meta">Dados gerais da empresa</small>
            </div>
          </div>
        </div>
      </div>
    </section>

    <div class="row dashboard-layout">
      <div class="col-12 col-xxl-4 dashboard-billing-column">
        <MetricsMonthBillingGraphic />
      </div>

      <div class="col-12 col-xxl-8 dashboard-insights-column">
        <CardsDashboardMonth />
      </div>

      <div class="col-12">
        <MetricsMonthSendAndReceivedHour />
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import { defineComponent } from "vue";
import { mapActions, mapGetters } from "vuex";

import MetricsMonthBillingGraphic from "../../components/Dashboard/MetricsMonthBillingGraphic.vue";
import CardsDashboardMonth from "../../components/Dashboard/CardsDashboardMonth.vue";
import MetricsMonthSendAndReceivedHour from "../../components/Dashboard/MetricsMonthSendAndReceivedHour.vue";

export default defineComponent({
  name: "DashboardPage",
  components: {
    MetricsMonthBillingGraphic,
    CardsDashboardMonth,
    MetricsMonthSendAndReceivedHour,
  },
  data() {
    return {
      intervalId: null as ReturnType<typeof setInterval> | null,
    };
  },
  computed: {
    ...mapGetters(["getCardsDashboardMonth", "getMetricsMonthBillingGraphic", "getDashboardInsightsQuery", "getEmployeeMetricsScope"]),
    overviewPaymentTax() {
      const billingTax = Number((this as any).getMetricsMonthBillingGraphic?.tax);
      const cardsTax = Number((this as any).getCardsDashboardMonth?.tax);

      if (!Number.isNaN(billingTax) && billingTax > 0) {
        return billingTax;
      }

      if (!Number.isNaN(cardsTax) && cardsTax > 0) {
        return cardsTax;
      }

      return 0;
    },
  },
  mounted() {
    this.fetchOperatingHours();
    this.refreshDashboard();
    this.intervalId = setInterval(() => {
      this.refreshDashboard();
    }, 30000);
  },
  watch: {
    getEmployeeMetricsScope(value) {
      if (value) this.refreshDashboard();
    },
  },
  methods: {
    ...mapActions([
      "fetchOperatingHours",
      "fetchMetricsMonthBillingGraphic",
      "fetchCardsDashboardMonth",
      "fetchDashboardInsights",
      "fetchMetricsMonthScheduleCategoriesHour",
    ]),
    formatCurrency(value: number | string) {
      return new Intl.NumberFormat("pt-BR", {
        style: "currency",
        currency: "BRL",
        minimumFractionDigits: 2,
      }).format(Number(value) || 0);
    },
    destroyInterval() {
      if (this.intervalId) {
        clearInterval(this.intervalId);
      }
    },
    refreshDashboard() {
      this.fetchMetricsMonthBillingGraphic();
      this.fetchCardsDashboardMonth();
      this.fetchMetricsMonthScheduleCategoriesHour();
    },
  },
  beforeUnmount() {
    this.destroyInterval();
  },
});
</script>

<style scoped>
@media (min-width: 1400px) {
  .dashboard-billing-column,
  .dashboard-insights-column {
    display: flex;
  }

  .dashboard-billing-column :deep(.billing-card) {
    flex: 1;
    min-width: 0;
    margin-bottom: 18px !important;
  }

  .dashboard-billing-column :deep(.billing-card .chart) {
    flex: 1;
    align-items: center;
  }

  .dashboard-insights-column :deep(.dashboard-month-grid) {
    flex: 1;
    min-width: 0;
  }
}

.dashboard-overview .card-body {
  padding: 20px 22px;
}

.dashboard-overview__content {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 18px;
}

.dashboard-overview__eyebrow {
  display: inline-block;
  margin-bottom: 6px;
  color: #6560f0;
  font-size: 12px;
  font-weight: 700;
  letter-spacing: 0.08em;
  text-transform: uppercase;
}

.dashboard-overview__title {
  margin: 0 0 6px;
  font-size: 28px;
  line-height: 1.1;
}

.dashboard-overview__description {
  margin: 0;
  max-width: 520px;
  color: #5b5f7b;
}

.dashboard-overview__stats {
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  gap: 12px;
  min-width: min(100%, 560px);
}

.dashboard-overview__stat {
  border: 1px solid #eef0f7;
  border-radius: 7px;
  padding: 14px;
  background: #fff;
}

.dashboard-overview__stat-label {
  display: block;
  margin-bottom: 6px;
  color: #8e8da1;
  font-size: 12px;
  text-transform: uppercase;
}

.dashboard-overview__stat strong {
  display: block;
  color: #2b2a3f;
  font-size: 20px;
  line-height: 1.1;
}

.dashboard-overview__stat-meta {
  display: block;
  margin-top: 4px;
  color: #8e8da1;
  font-size: 11px;
  line-height: 1.2;
}

@media (max-width: 991px) {
  .dashboard-overview__content {
    flex-direction: column;
    align-items: stretch;
  }

  .dashboard-overview__stats {
    min-width: 0;
  }
}

@media (max-width: 767px) {
  .dashboard-overview .card-body {
    padding: 18px 16px;
  }

  .dashboard-overview__title {
    font-size: 22px;
  }

  .dashboard-overview__description {
    font-size: 13px;
  }

  .dashboard-overview__stats {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }

  .dashboard-overview__stat--wide {
    grid-column: span 1;
  }

  .dashboard-overview__stat {
    padding: 12px;
  }

  .dashboard-overview__stat strong {
    font-size: 18px;
  }
}
</style>

<style>
body.dark .dashboard-page .dashboard-overview__description {
  color: #b8b6cf;
}

body.dark .dashboard-page .dashboard-overview__stat {
  background: #2b2a3f;
  border-color: #45445f;
}

body.dark .dashboard-page .dashboard-overview__stat-label,
body.dark .dashboard-page .dashboard-overview__stat-meta {
  color: #b8b6cf;
}

body.dark .dashboard-page .dashboard-overview__stat strong {
  color: #ffffff;
}
</style>
